export type Timeframe =
  | 'Today'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'Month to Date'
  | 'Custom Range'

export interface MetricData {
  name: string
  value: number | string
  trend: 'up' | 'down' | 'neutral'
  trendValue: string
  sparklineData: { value: number }[]
}

export interface FunnelStage {
  name: string
  percentage: number
  status: 'green' | 'yellow' | 'red'
}

export interface ProjectionData {
  lockTarget: number
  lockActual: number
  commissionTarget: number
  commissionActual: number
  pipelineCoverage: number
}

export interface ScorecardMetric {
  name: string
  actual: number
  target: number
  status: 'above' | 'below' | 'meet'
}

export interface LeaderboardEntry {
  rank: number
  name: string
  locks: number
  submissions: number
  score: number
  isCurrentUser?: boolean
}

export interface GamificationData {
  streaks: {
    daily: number
    pull: number
    submission: number
    lock: number
  }
  badges: {
    name: string
    earned: boolean
    icon: string
  }[]
  closerOfWeek: {
    name: string
    locks: number
  }
}

export interface DashboardData {
  metrics: MetricData[]
  funnel: FunnelStage[]
  projections: ProjectionData
  scorecard: ScorecardMetric[]
  leaderboard: LeaderboardEntry[]
  gamification: GamificationData
}

const generateSparkline = (
  base: number,
  volatility: number,
  trend: 'up' | 'down',
) => {
  const data = []
  let current = base
  for (let i = 0; i < 10; i++) {
    const change = (Math.random() - (trend === 'up' ? 0.3 : 0.7)) * volatility
    current = Math.max(0, current + change)
    data.push({ value: current })
  }
  return data
}

export const mockDataByTimeframe: Record<Timeframe, DashboardData> = {
  Today: {
    metrics: [
      {
        name: 'Dials',
        value: 45,
        trend: 'up',
        trendValue: '+12%',
        sparklineData: generateSparkline(30, 5, 'up'),
      },
      {
        name: 'Conversations',
        value: 28,
        trend: 'up',
        trendValue: '+8%',
        sparklineData: generateSparkline(20, 4, 'up'),
      },
      {
        name: 'Credit Pulls',
        value: 12,
        trend: 'down',
        trendValue: '-5%',
        sparklineData: generateSparkline(15, 3, 'down'),
      },
      {
        name: 'Applications',
        value: 8,
        trend: 'up',
        trendValue: '+15%',
        sparklineData: generateSparkline(5, 2, 'up'),
      },
      {
        name: 'Submissions',
        value: 5,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(5, 1, 'up'),
      },
      {
        name: 'Locks',
        value: 3,
        trend: 'up',
        trendValue: '+50%',
        sparklineData: generateSparkline(1, 1, 'up'),
      },
    ],
    funnel: [
      { name: 'Pull → App', percentage: 67, status: 'yellow' },
      { name: 'App → Sub', percentage: 63, status: 'yellow' },
      { name: 'Sub → Sign', percentage: 80, status: 'green' },
      { name: 'Sign → Lock', percentage: 60, status: 'yellow' },
    ],
    projections: {
      lockTarget: 12,
      lockActual: 8,
      commissionTarget: 25000,
      commissionActual: 18500,
      pipelineCoverage: 1.8,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 67, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 63, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 80, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      { rank: 1, name: 'Michael Chang', locks: 15, submissions: 22, score: 98 },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        locks: 14,
        submissions: 20,
        score: 95,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        locks: 12,
        submissions: 18,
        score: 92,
        isCurrentUser: true,
      },
      { rank: 4, name: 'Emily Davis', locks: 11, submissions: 19, score: 89 },
      { rank: 5, name: 'David Wilson', locks: 10, submissions: 15, score: 85 },
      { rank: 6, name: 'Jessica Taylor', locks: 9, submissions: 14, score: 82 },
      { rank: 7, name: 'James Brown', locks: 8, submissions: 12, score: 78 },
      { rank: 8, name: 'Linda Martinez', locks: 7, submissions: 11, score: 75 },
      {
        rank: 9,
        name: 'Robert Anderson',
        locks: 6,
        submissions: 10,
        score: 70,
      },
      { rank: 10, name: 'William Thomas', locks: 5, submissions: 8, score: 65 },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: false, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 7 },
    },
  },
  'Last 7 Days': {
    metrics: [
      {
        name: 'Dials',
        value: 315,
        trend: 'up',
        trendValue: '+18%',
        sparklineData: generateSparkline(250, 20, 'up'),
      },
      {
        name: 'Conversations',
        value: 196,
        trend: 'up',
        trendValue: '+12%',
        sparklineData: generateSparkline(150, 15, 'up'),
      },
      {
        name: 'Credit Pulls',
        value: 84,
        trend: 'up',
        trendValue: '+5%',
        sparklineData: generateSparkline(70, 8, 'up'),
      },
      {
        name: 'Applications',
        value: 56,
        trend: 'up',
        trendValue: '+22%',
        sparklineData: generateSparkline(40, 5, 'up'),
      },
      {
        name: 'Submissions',
        value: 35,
        trend: 'up',
        trendValue: '+10%',
        sparklineData: generateSparkline(25, 4, 'up'),
      },
      {
        name: 'Locks',
        value: 21,
        trend: 'up',
        trendValue: '+40%',
        sparklineData: generateSparkline(10, 3, 'up'),
      },
    ],
    funnel: [
      { name: 'Pull → App', percentage: 72, status: 'green' },
      { name: 'App → Sub', percentage: 68, status: 'yellow' },
      { name: 'Sub → Sign', percentage: 85, status: 'green' },
      { name: 'Sign → Lock', percentage: 65, status: 'yellow' },
    ],
    projections: {
      lockTarget: 25,
      lockActual: 21,
      commissionTarget: 50000,
      commissionActual: 42000,
      pipelineCoverage: 2.1,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 72, target: 70, status: 'above' },
      { name: 'App-to-Sub %', actual: 68, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 85, target: 75, status: 'above' },
      { name: 'Overall %', actual: 28, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Sarah Mitchell',
        locks: 32,
        submissions: 45,
        score: 99,
      },
      { rank: 2, name: 'Michael Chang', locks: 28, submissions: 40, score: 96 },
      {
        rank: 3,
        name: 'Alex Johnson',
        locks: 25,
        submissions: 38,
        score: 94,
        isCurrentUser: true,
      },
      { rank: 4, name: 'Emily Davis', locks: 22, submissions: 35, score: 90 },
      { rank: 5, name: 'David Wilson', locks: 20, submissions: 30, score: 86 },
      {
        rank: 6,
        name: 'Jessica Taylor',
        locks: 18,
        submissions: 28,
        score: 83,
      },
      { rank: 7, name: 'James Brown', locks: 15, submissions: 25, score: 79 },
      {
        rank: 8,
        name: 'Linda Martinez',
        locks: 14,
        submissions: 22,
        score: 76,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        locks: 12,
        submissions: 20,
        score: 72,
      },
      {
        rank: 10,
        name: 'William Thomas',
        locks: 10,
        submissions: 18,
        score: 68,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 14 },
    },
  },
  'Last 30 Days': {
    metrics: [
      {
        name: 'Dials',
        value: 1250,
        trend: 'down',
        trendValue: '-2%',
        sparklineData: generateSparkline(1300, 50, 'down'),
      },
      {
        name: 'Conversations',
        value: 780,
        trend: 'up',
        trendValue: '+4%',
        sparklineData: generateSparkline(700, 40, 'up'),
      },
      {
        name: 'Credit Pulls',
        value: 340,
        trend: 'down',
        trendValue: '-8%',
        sparklineData: generateSparkline(380, 20, 'down'),
      },
      {
        name: 'Applications',
        value: 210,
        trend: 'up',
        trendValue: '+5%',
        sparklineData: generateSparkline(190, 15, 'up'),
      },
      {
        name: 'Submissions',
        value: 145,
        trend: 'up',
        trendValue: '+12%',
        sparklineData: generateSparkline(120, 10, 'up'),
      },
      {
        name: 'Locks',
        value: 85,
        trend: 'up',
        trendValue: '+18%',
        sparklineData: generateSparkline(65, 8, 'up'),
      },
    ],
    funnel: [
      { name: 'Pull → App', percentage: 62, status: 'yellow' },
      { name: 'App → Sub', percentage: 69, status: 'yellow' },
      { name: 'Sub → Sign', percentage: 82, status: 'green' },
      { name: 'Sign → Lock', percentage: 71, status: 'green' },
    ],
    projections: {
      lockTarget: 100,
      lockActual: 85,
      commissionTarget: 200000,
      commissionActual: 175000,
      pipelineCoverage: 2.4,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 62, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 69, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 82, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Michael Chang',
        locks: 110,
        submissions: 180,
        score: 97,
      },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        locks: 105,
        submissions: 175,
        score: 96,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        locks: 95,
        submissions: 160,
        score: 93,
        isCurrentUser: true,
      },
      { rank: 4, name: 'Emily Davis', locks: 88, submissions: 150, score: 89 },
      { rank: 5, name: 'David Wilson', locks: 82, submissions: 140, score: 85 },
      {
        rank: 6,
        name: 'Jessica Taylor',
        locks: 75,
        submissions: 130,
        score: 81,
      },
      { rank: 7, name: 'James Brown', locks: 68, submissions: 115, score: 77 },
      {
        rank: 8,
        name: 'Linda Martinez',
        locks: 60,
        submissions: 105,
        score: 74,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        locks: 55,
        submissions: 95,
        score: 70,
      },
      {
        rank: 10,
        name: 'William Thomas',
        locks: 48,
        submissions: 85,
        score: 65,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: true, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Michael Chang', locks: 28 },
    },
  },
  'Month to Date': {
    metrics: [
      {
        name: 'Dials',
        value: 650,
        trend: 'up',
        trendValue: '+5%',
        sparklineData: generateSparkline(600, 30, 'up'),
      },
      {
        name: 'Conversations',
        value: 410,
        trend: 'up',
        trendValue: '+8%',
        sparklineData: generateSparkline(350, 20, 'up'),
      },
      {
        name: 'Credit Pulls',
        value: 180,
        trend: 'up',
        trendValue: '+12%',
        sparklineData: generateSparkline(150, 15, 'up'),
      },
      {
        name: 'Applications',
        value: 115,
        trend: 'up',
        trendValue: '+15%',
        sparklineData: generateSparkline(90, 10, 'up'),
      },
      {
        name: 'Submissions',
        value: 75,
        trend: 'up',
        trendValue: '+20%',
        sparklineData: generateSparkline(55, 8, 'up'),
      },
      {
        name: 'Locks',
        value: 45,
        trend: 'up',
        trendValue: '+25%',
        sparklineData: generateSparkline(30, 5, 'up'),
      },
    ],
    funnel: [
      { name: 'Pull → App', percentage: 64, status: 'yellow' },
      { name: 'App → Sub', percentage: 65, status: 'yellow' },
      { name: 'Sub → Sign', percentage: 88, status: 'green' },
      { name: 'Sign → Lock', percentage: 68, status: 'yellow' },
    ],
    projections: {
      lockTarget: 60,
      lockActual: 45,
      commissionTarget: 120000,
      commissionActual: 95000,
      pipelineCoverage: 2.2,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 64, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 65, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 88, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Sarah Mitchell',
        locks: 55,
        submissions: 90,
        score: 98,
      },
      { rank: 2, name: 'Michael Chang', locks: 52, submissions: 85, score: 95 },
      {
        rank: 3,
        name: 'Alex Johnson',
        locks: 48,
        submissions: 80,
        score: 92,
        isCurrentUser: true,
      },
      { rank: 4, name: 'Emily Davis', locks: 42, submissions: 75, score: 88 },
      { rank: 5, name: 'David Wilson', locks: 38, submissions: 65, score: 84 },
      {
        rank: 6,
        name: 'Jessica Taylor',
        locks: 35,
        submissions: 60,
        score: 81,
      },
      { rank: 7, name: 'James Brown', locks: 30, submissions: 55, score: 76 },
      {
        rank: 8,
        name: 'Linda Martinez',
        locks: 28,
        submissions: 50,
        score: 73,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        locks: 25,
        submissions: 45,
        score: 69,
      },
      {
        rank: 10,
        name: 'William Thomas',
        locks: 20,
        submissions: 35,
        score: 64,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 18 },
    },
  },
  'Custom Range': {
    metrics: [
      {
        name: 'Dials',
        value: 850,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(850, 10, 'neutral'),
      },
      {
        name: 'Conversations',
        value: 520,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(520, 10, 'neutral'),
      },
      {
        name: 'Credit Pulls',
        value: 240,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(240, 5, 'neutral'),
      },
      {
        name: 'Applications',
        value: 160,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(160, 5, 'neutral'),
      },
      {
        name: 'Submissions',
        value: 105,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(105, 3, 'neutral'),
      },
      {
        name: 'Locks',
        value: 65,
        trend: 'neutral',
        trendValue: '0%',
        sparklineData: generateSparkline(65, 2, 'neutral'),
      },
    ],
    funnel: [
      { name: 'Pull → App', percentage: 66, status: 'yellow' },
      { name: 'App → Sub', percentage: 65, status: 'yellow' },
      { name: 'Sub → Sign', percentage: 84, status: 'green' },
      { name: 'Sign → Lock', percentage: 73, status: 'green' },
    ],
    projections: {
      lockTarget: 80,
      lockActual: 65,
      commissionTarget: 160000,
      commissionActual: 135000,
      pipelineCoverage: 2.0,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 66, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 65, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 84, target: 75, status: 'above' },
      { name: 'Overall %', actual: 27, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Michael Chang',
        locks: 85,
        submissions: 130,
        score: 96,
      },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        locks: 82,
        submissions: 125,
        score: 95,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        locks: 75,
        submissions: 115,
        score: 92,
        isCurrentUser: true,
      },
      { rank: 4, name: 'Emily Davis', locks: 68, submissions: 105, score: 88 },
      { rank: 5, name: 'David Wilson', locks: 62, submissions: 95, score: 85 },
      {
        rank: 6,
        name: 'Jessica Taylor',
        locks: 58,
        submissions: 90,
        score: 82,
      },
      { rank: 7, name: 'James Brown', locks: 52, submissions: 80, score: 78 },
      {
        rank: 8,
        name: 'Linda Martinez',
        locks: 48,
        submissions: 75,
        score: 75,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        locks: 42,
        submissions: 65,
        score: 71,
      },
      {
        rank: 10,
        name: 'William Thomas',
        locks: 35,
        submissions: 55,
        score: 66,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Michael Chang', locks: 22 },
    },
  },
}
